import { createFileRoute, redirect } from "@tanstack/react-router";
import type { ClientLocationPlanBaseData } from "api-types/locationPlans/locationPlansResponses";
import type { Builder } from "api-types/modelTypes";
import { WEB_API_BASE_PATH } from "common/constants";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import Showroom from "lib/components/builders/communities/plans/showroom/Showroom";
import LoadingPage from "lib/components/layout/LoadingPage";
import { getBuilderLocationPlanURL } from "lib/endpointHelpers";
import { useFetchImmutable } from "lib/fetcher";
import { useAppRouterParams } from "lib/routeHelpers";
import getPageTitle from "lib/utils/getPageTitle";
import { createLocationPlanData } from "lib/utils/showroom/showroomData";
import { Helmet as Head } from "react-helmet-async";
import withAuthentication from "../../../../../../../components/withAuthentication";

function CommunityPlanShowroomPage() {
  const { isReady, builderId, locationId, locationPlanId } = useAppRouterParams();

  if (!isReady || !builderId || !locationId || !locationPlanId) {
    return <LoadingPage />;
  }

  return (
    <ReadyCommunityPlanShowroomPage builderId={builderId} locationId={locationId} locationPlanId={locationPlanId} />
  );
}

function ReadyCommunityPlanShowroomPage({
  builderId,
  locationId,
  locationPlanId,
}: {
  builderId: string;
  locationId: string;
  locationPlanId: string;
}) {
  const { data: locationPlanBaseData, loading: loadingCommunityPlan } = useFetchImmutable<ClientLocationPlanBaseData>(
    getBuilderLocationPlanURL({ builderId, locationId, locationPlanId }),
    [builderId, locationId, locationPlanId],
  );
  const { data: builder, loading: loadingBuilder } = useFetchImmutable<Builder>(
    `${WEB_API_BASE_PATH}/builders/${builderId}`,
    [builderId],
  );

  if (loadingBuilder || loadingCommunityPlan) {
    return <LoadingPage />;
  }

  assertIsNotUndefined(locationPlanBaseData);
  const { masterPlan, locationPlan, locationHydrated, homePlanHydrated, elevationOverrides } =
    createLocationPlanData(locationPlanBaseData);

  return (
    <>
      <Head>
        <title>{getPageTitle(`Configure ${locationPlanBaseData.locationPlan.name}`)}</title>
        <meta name="robots" content="noindex" />
      </Head>
      <Showroom
        builder={builder}
        masterPlan={masterPlan}
        location={locationHydrated}
        locationPlan={locationPlan}
        homePlan={homePlanHydrated}
        lot={undefined}
        previewMode
        lotDiscounts={[]}
        homePlanDiscounts={[]}
        reportingUnitLots={locationPlanBaseData.reportingUnitLots}
        elevationOverrides={elevationOverrides}
      />
    </>
  );
}

export const Route = createFileRoute(
  "/builders/$builder_id/locations/$location_id/location-plans/$location_plan_id/showroom",
)({
  beforeLoad: ({ context, params }) => {
    if (!context.enabledFeatures.showroomUnification) {
      return;
    }
    throw redirect({
      to: "/showroom",
      search: { builderId: params.builder_id, planType: "community_plans", planId: params.location_plan_id },
      replace: true,
    });
  },
  component: withAuthentication(CommunityPlanShowroomPage, {
    products: ["showroom"],
  }),
});
